/**
 * Granular synthesis preview, backed by Tone.js GrainPlayer.
 *
 * Why a separate module / why Tone.js:
 *   - GrainPlayer decouples pitch from playback rate via overlapping
 *     short audio "grains" with their own envelopes, so we can pitch
 *     a sample up an octave without speeding it up (or vice versa).
 *     Same family of techniques powers Beetlecrab Tempera and most
 *     hardware granular instruments.
 *   - Implementing a phase-vocoder or WSOLA stretcher from scratch
 *     would be a multi-week DSP project. Tone.GrainPlayer is the
 *     proven, browser-tested wrapper.
 *
 * Why a "preview" rather than a sequencer pad type yet:
 *   - This is the first integration of Tone.js into the app. Bundle
 *     impact, AudioContext compatibility (we manage our own context
 *     for the rest of the audio engine, Tone manages its own
 *     internally), and WKWebView quirks need to be validated before
 *     we wire granular into the trigger-time hot path. The preview
 *     panel in the slicer is the validation surface.
 *
 * Tone.js context note:
 *   GrainPlayer routes audio through Tone's globally-managed
 *   `Tone.context`. Our existing engine routes through its own
 *   AudioContext singleton (see useAudioEngine.ts). For the preview
 *   we accept the parallel context — both contexts mix at the
 *   destination, which the OS combines without artifact. If we ever
 *   integrate granular into the per-step trigger path, we can call
 *   `Tone.setContext(ourCtx)` at engine init so they share — that's
 *   a cheap follow-up, not a blocker for the MVP.
 */

import { GrainPlayer, ToneAudioBuffer } from 'tone'

export interface GranularParams {
  /** Position to grain-trigger from, in seconds into the source. */
  positionSec: number
  /** Length of each grain in seconds. Smaller = more granular
   *  texture; larger = closer to natural playback. Tempera uses
   *  ~50–500ms; we default to 100ms. */
  grainSize: number
  /** Crossfade between adjacent grains in seconds. Larger overlaps
   *  smooth out grain boundaries; smaller overlaps preserve
   *  transients but can click on tonal material. */
  overlap: number
  /** Pitch shift in semitones. -24..+24 is the slider range; the
   *  GrainPlayer decouples this from `playbackRate`. */
  pitchSemitones: number
  /** Playback rate multiplier. 1.0 = native tempo; 0.5 = half
   *  speed; 2.0 = double. Decoupled from pitch by GrainPlayer —
   *  this is the "Stretcher" primitive that lets the user conform
   *  a loop to project BPM interactively without the Rubber Band
   *  offline round-trip. Range 0.25 – 4.0. */
  playbackRate?: number
  /** Playback duration in seconds. After this, the GrainPlayer
   *  stops emitting grains. Pass Infinity for "play until stop()". */
  durationSec?: number
  /** Loop within the grain region. False by default — the
   *  preview's primary use is "audition slice X with grain
   *  smoothing" which is one-shot. */
  loop?: boolean
  /** Loop region start in seconds. Only takes effect when `loop`
   *  is true. The ScrubPad uses this to create a small grain
   *  region around the scrub puck so emission is locked to a
   *  slice of the source no matter how long the playback runs.
   *  Leave undefined to loop the whole buffer. */
  loopStart?: number
  /** Loop region end in seconds. See `loopStart`. */
  loopEnd?: number
  /** Play the source in reverse. Distinct from per-step
   *  `reverse` on the sequencer — this is the GrainPlayer's own
   *  primitive, lets a pad / swell run backward through the
   *  sample without re-rendering the buffer. */
  reverse?: boolean
}

/** Mode presets — seed sensible defaults for the two primary ways
 *  this module gets used. The UI layer picks one and the caller
 *  merges in the per-instance parameters. */
export const GRANULAR_PRESETS = {
  /** Jungle chop / MPC-style per-slice trigger. Tight grains, no
   *  overlap (transients stay crisp), native tempo. */
  chopper: {
    grainSize: 0.03, // 30 ms
    overlap: 0, // breakcore crunch
    playbackRate: 1,
    loop: false,
  },
  /** Conform-to-tempo / slow-down / pitch-shift. Longer grains and
   *  moderate overlap smooth out the time-stretch artifacts; loop
   *  on so the user can audition the whole sample endlessly. */
  stretcher: {
    grainSize: 0.1, // 100 ms
    overlap: 0.1,
    playbackRate: 1,
    loop: true,
  },
} as const satisfies Record<string, Partial<GranularParams>>

/**
 * Sequencer-pad granular presets. Shaped to seed a
 * `GranularPadContent` directly — fields match the pad type
 * (`grainSizeMs` not `grainSize`, cents not semitones). The
 * PadEditor's preset pills spread one of these onto the pad's
 * draft without any unit conversion.
 *
 * Keep these minimal: they're seeds, not full pads. The pad
 * carries `sampleId` / `s3Key` / `midiNote` from the user's
 * picker choice; the preset supplies character.
 */
export interface GranularPadPreset {
  grainSizeMs: number
  overlap: number
  playbackRate: number
  reverse: boolean
  loop: boolean
  positionSpread: number
  pitchSpread: number
  detuneCents: number
}

export const GRANULAR_PAD_PRESETS = {
  /** Held tone — tiny position spread so the loop breathes, no
   *  pitch jitter. Ideal on a vowel, a pad, a synth stab. */
  freeze: {
    grainSizeMs: 300,
    overlap: 0.5,
    playbackRate: 1,
    reverse: false,
    loop: true,
    positionSpread: 0.02,
    pitchSpread: 0,
    detuneCents: 0,
  },
  /** Half-speed stretch with no randomisation — "play it slower"
   *  without pitch shift. One-shot (loop off). */
  stretch: {
    grainSizeMs: 150,
    overlap: 0.3,
    playbackRate: 0.5,
    reverse: false,
    loop: false,
    positionSpread: 0,
    pitchSpread: 0,
    detuneCents: 0,
  },
  /** Wide random position + pitch — the cloud / shimmer preset. */
  cloud: {
    grainSizeMs: 80,
    overlap: 0.4,
    playbackRate: 1,
    reverse: false,
    loop: true,
    positionSpread: 0.4,
    pitchSpread: 600,
    detuneCents: 0,
  },
  /** Backwards-swell — long overlapping reverse grains. Great on
   *  cymbal crashes, vocal tails. */
  'reverse-swell': {
    grainSizeMs: 500,
    overlap: 0.6,
    playbackRate: 1,
    reverse: true,
    loop: true,
    positionSpread: 0.1,
    pitchSpread: 100,
    detuneCents: 0,
  },
} as const satisfies Record<string, GranularPadPreset>

export type GranularPadPresetId = keyof typeof GRANULAR_PAD_PRESETS

/**
 * Lightweight granular preview — owns one GrainPlayer per buffer,
 * lets the caller drive its position / params live. Disposes Tone
 * resources on release.
 *
 * Lifecycle:
 *   const g = await createGranularPreview(audioBuffer)
 *   g.play({ positionSec: 0.5, grainSize: 0.1, overlap: 0.05,
 *            pitchSemitones: 0 })
 *   // ...adjust params live...
 *   g.update({ positionSec: 0.8 })
 *   g.stop()
 *   g.dispose()
 */
export interface GranularPreview {
  /** Trigger / re-trigger playback at the given params. Calling
   *  while already playing seeks to the new position and rebuilds
   *  grain emission. */
  play: (params: GranularParams) => void
  /** Update params on a currently-playing GrainPlayer without
   *  re-triggering — useful for live slider drags. Position seeks
   *  immediately; pitch/grainSize/overlap update for future
   *  grains. */
  update: (params: Partial<GranularParams>) => void
  /** Stop emitting grains. Doesn't dispose; play() can be called
   *  again. */
  stop: () => void
  /** Tear down. Safe to call multiple times. After dispose() the
   *  preview is unusable; call createGranularPreview() again to
   *  get a fresh one. */
  dispose: () => void
  /** Whether the underlying GrainPlayer is currently emitting. */
  isPlaying: () => boolean
}

/**
 * Convert an `AudioBuffer` from the standard Web Audio context
 * (which is what our engine produces via `decodeAudioData`) into
 * a `ToneAudioBuffer` so GrainPlayer can use it. Tone wraps it
 * cheaply — no audio bytes are copied; the underlying buffer is
 * referenced by both. `set` accepts an AudioBuffer directly.
 */
function asToneBuffer(buffer: AudioBuffer): ToneAudioBuffer {
  const tb = new ToneAudioBuffer()
  tb.set(buffer)
  return tb
}

export async function createGranularPreview(
  buffer: AudioBuffer,
): Promise<GranularPreview> {
  const toneBuffer = asToneBuffer(buffer)
  const player = new GrainPlayer(toneBuffer).toDestination()
  let playing = false

  const apply = (p: Partial<GranularParams>) => {
    if (p.grainSize !== undefined) {
      player.grainSize = Math.max(0.005, Math.min(2, p.grainSize))
    }
    if (p.overlap !== undefined) {
      player.overlap = Math.max(0, Math.min(1, p.overlap))
    }
    if (p.pitchSemitones !== undefined) {
      // Tone uses cents (1 semitone = 100 cents).
      player.detune = Math.max(-2400, Math.min(2400, p.pitchSemitones * 100))
    }
    if (p.playbackRate !== undefined) {
      // GrainPlayer.playbackRate is a plain number (not an
      // AudioParam) — clamp to the same 0.25–4 range the Tempera-
      // inspired UI exposes. Pitch is handled by `detune` above, so
      // changing the rate here doesn't pitch-shift.
      player.playbackRate = Math.max(0.25, Math.min(4, p.playbackRate))
    }
    if (p.loop !== undefined) {
      player.loop = p.loop
    }
    if (p.loopStart !== undefined) {
      // GrainPlayer exposes `loopStart` / `loopEnd` as Time values.
      // Seconds are accepted directly. Clamp to the source length.
      player.loopStart = Math.max(0, Math.min(buffer.duration, p.loopStart))
    }
    if (p.loopEnd !== undefined) {
      player.loopEnd = Math.max(0, Math.min(buffer.duration, p.loopEnd))
    }
    if (p.reverse !== undefined) {
      player.reverse = p.reverse
    }
  }

  return {
    play: params => {
      apply(params)
      // Re-triggering: stop any in-flight emission, then start
      // from the new position. Tone.js requires the AudioContext
      // to be resumed in a user-gesture callback before start();
      // the slicer's "Play" button is exactly that gesture, so
      // this is safe in our intended call site.
      if (playing) player.stop()
      const offset = Math.max(0, Math.min(buffer.duration, params.positionSec))
      const dur =
        params.durationSec !== undefined && params.durationSec > 0
          ? params.durationSec
          : undefined
      player.start(undefined, offset, dur)
      playing = true
    },
    update: params => {
      apply(params)
      if (params.positionSec !== undefined && playing) {
        // GrainPlayer doesn't expose a `seek()` per se — restart
        // from the new offset to move the playhead.
        const offset = Math.max(
          0,
          Math.min(buffer.duration, params.positionSec),
        )
        player.stop()
        player.start(undefined, offset)
      }
    },
    stop: () => {
      if (playing) {
        player.stop()
        playing = false
      }
    },
    dispose: () => {
      try {
        player.stop()
      } catch {
        /* already stopped is fine */
      }
      player.dispose()
      toneBuffer.dispose()
      playing = false
    },
    isPlaying: () => playing,
  }
}
